import moment from 'moment';

import { Pet as PetType, SolicitudCambioPetReq } from '../types/Pet/types';

const getEdad = (fechaNacimiento?: string): string => {
  if (!fechaNacimiento) return '';

  const now = moment();
  const nacimiento = moment(fechaNacimiento);

  const years = now.diff(nacimiento, 'years');
  const months = now.diff(nacimiento, 'months');

  if (years > 0) {
    return `${years} año${years === 1 ? '' : 's'}`;
  }

  return `${months} mes${months === 1 ? '' : 'es'}`;
};

// Backend garantiza orden por fecha
const getPeso = (pet) => pet?.historial_peso?.at(-1)?.peso ?? null;

/**
 * Calcula u obtiene el consumo diario (en gramos) de una mascota.
 *
 * Prioridad:
 * 1) Consumo directo desde bolsa/alimento (foods + bagId)
 * 2) Consumo definido manualmente (consumos + mascotaId)
 * 3) Cálculo estimado por fórmula (peso + carga energética)
 *
 * Si no puede resolverse, devuelve 0.
 */
const resolveConsumoDiario = (params: {
  mascotaId?: number;
  bagId?: number;
  peso?: number;
  cargaEnergetica?: number;
  consumos?: { mascotaId: number; consumoDiario: number }[];
  foods?: any[]; // FoodRes[]
}): number => {
  const { mascotaId, bagId, peso, cargaEnergetica, consumos, foods } = params;

  // 1️⃣ Consumo desde bolsa
  if (bagId && foods?.length) {
    for (const food of foods) {
      const stock = food?.stocksAlimentoMascotas?.[0];
      if (stock?.stockAlimentoId === bagId) {
        return stock.consume ?? 0;
      }
    }
  }

  // 2️⃣ Consumo manual por mascota
  if (mascotaId && consumos?.length) {
    const consumo = consumos.find((c) => c.mascotaId === mascotaId);
    if (consumo) return consumo.consumoDiario;
  }

  // 3️⃣ Cálculo estimado
  if (peso && cargaEnergetica) {
    return (145 * Math.pow(peso, 0.67) * 1000) / cargaEnergetica;
  }

  return 0;
};

// Mapeo: Pet original -> body solicitud
// Solo campos editables / relevantes para solicitud de cambio
const PET_FIELD_MAP: Partial<Record<keyof PetType, keyof SolicitudCambioPetReq>> = {
  nombre: 'mascotaNombre',
  color: 'mascotaColor',
  sexo: 'mascotaSexo',
  fecha_nacimiento: 'mascotaFechaNac',
  animal: 'mascotaEspecie',
  raza: 'mascotaRaza',
};

// Normalizar valor para comparación
const normalizeValue = (val: unknown): string => {
  if (val === null || val === undefined) return '';

  return String(val).trim().toLowerCase();
};
// Comparar mascota original con el body de solicitud
const hasChangesInPetBody = (
  originalPet: PetType | undefined,
  body: SolicitudCambioPetReq,
): boolean => {
  if (!originalPet) return false;

  for (const [petKey, bodyKey] of Object.entries(PET_FIELD_MAP)) {
    const originalVal = normalizeValue(originalPet[petKey as keyof PetType]);

    const bodyVal = normalizeValue(body[bodyKey as keyof SolicitudCambioPetReq]);

    if (originalVal !== bodyVal) {
      return true;
    }
  }

  return false;
};

export { getEdad, getPeso, resolveConsumoDiario, hasChangesInPetBody };
